"use client";

import { useSession, signOut, getSession } from "next-auth/react";
import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { usePathname } from "next/navigation";
import { useXNoticeStore } from "@/components/context/xnotice-store";

export const SESSION_FLAG_KEY = "offerz-had-session";
export const USER_TOKEN_KEY = "offerz-user-app-token";
export const GUEST_LOGIN_TRANSITION_KEY = "offerz-guest-login-transition";
export const GUEST_LOGIN_TRANSITION_COOKIE = "offerz-guest-login-transition";
export const GUEST_CART_MERGE_KEY = "offerz-guest-cart-merge";
export const SESSION_CHECK_INTERVAL_SEC = 60;
// Safety-net interval that catches clock drift / background-tab throttling /
// laptop sleep edge cases where the precise expiry setTimeout may not fire on time.
const EXPIRY_SAFETY_INTERVAL_MS = 5 * 1000;
// Small buffer so the timer fires *just* past the recorded expiry, avoiding a no-op
// where Date.now() is still <= s.expires by a millisecond when the callback runs.
const EXPIRY_TIMER_BUFFER_MS = 500;

function hadPriorSession(wasAuthenticated: boolean): boolean {
  return wasAuthenticated || !!localStorage.getItem(SESSION_FLAG_KEY);
}

export default function SessionMonitor() {
  const { data: session, status } = useSession();
  const wasAuthenticatedRef = useRef(false);
  const sessionRef = useRef(session);
  const logoutInProgressRef = useRef(false);
  // Snapshot of the user's encrypted app_token captured while authenticated, so that
  // the backend logout call still identifies the right user even if the cookie has
  // expired or been replaced with an anonymous token by the time the session ends.
  const userAppTokenRef = useRef<string | null>(null);
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { setCartCategoryIDs, setCartProductIDs } = useXNoticeStore();
  const isGuestToExistingTransition = useCallback(() => {
    return (
      localStorage.getItem(GUEST_LOGIN_TRANSITION_KEY) === "1" ||
      localStorage.getItem(GUEST_CART_MERGE_KEY) === "1" ||
      Cookies.get(GUEST_LOGIN_TRANSITION_COOKIE) === "1"
    );
  }, []);

  const performSessionExpiredLogout = useCallback(async () => {
    if (logoutInProgressRef.current) return;
    if (isGuestToExistingTransition()) return;
    logoutInProgressRef.current = true;
    localStorage.removeItem(SESSION_FLAG_KEY);
    wasAuthenticatedRef.current = false;

    const lang = Cookies.get("locale") ?? "de";

    // Prefer the in-memory ref (same session), then the localStorage backup (survives
    // page reloads / "next day" case), then the live cookie (last resort — may already
    // be an anonymous token set by the proxy middleware).
    const capturedAppToken =
      userAppTokenRef.current ??
      localStorage.getItem(USER_TOKEN_KEY) ??
      Cookies.get("app_token") ??
      null;

    // Clear cart immediately — before the async backend call — so stale items
    // never appear in the window between session detection and signOut redirect.
    setCartCategoryIDs([]);
    setCartProductIDs([]);
    queryClient.setQueriesData(
      { queryKey: ["cartQuantity"] },
      {
        success: 1,
        data: { quantity: 0, category_ids: [], product_ids: [] },
      },
    );
    queryClient.setQueryData(["cart"], {
      success: 1,
      data: {
        products: [],
        totals: [],
        total: 0,
        total_raw: 0,
        total_product_count: 0,
        total_loyalty_points: 0,
        text_items: 0,
        has_shipping: 0,
        has_download: 0,
        vouchers: [],
        coupon_status: 1,
        coupon: null,
        voucher_status: 1,
        voucher: null,
        category_ids: [],
        product_ids: [],
      },
    });

    userAppTokenRef.current = null;
    localStorage.removeItem(USER_TOKEN_KEY);

    try {
      await fetch("/api/auth/backend-logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appToken: capturedAppToken, lang }),
        keepalive: true,
      });
    } catch {
      // Still proceed with client-side signOut when API logout fails.
    }

    // Remove the app_token cookie client-side before redirecting so the
    // middleware mints a fresh anonymous token on the next /api/auth/session
    // request. Without this, the browser keeps the user's old token and the
    // next page load fetches cart data as that user.
    Cookies.remove("app_token", { path: "/" });

    await signOut({ redirect: false });
    logoutInProgressRef.current = false;
  }, [isGuestToExistingTransition, queryClient, setCartCategoryIDs, setCartProductIDs]);

  const reconcileStaleSession = useCallback(async () => {
    if (logoutInProgressRef.current) return;
    if (isGuestToExistingTransition()) return;
    if (!hadPriorSession(wasAuthenticatedRef.current)) return;

    const liveSession = await getSession();
    if (!liveSession?.user) {
      await performSessionExpiredLogout();
    }
  }, [isGuestToExistingTransition, performSessionExpiredLogout]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    if (status === "authenticated") {
      wasAuthenticatedRef.current = true;
      localStorage.setItem(SESSION_FLAG_KEY, "1");
      const currentToken = Cookies.get("app_token");
      if (currentToken) {
        userAppTokenRef.current = currentToken;
        localStorage.setItem(USER_TOKEN_KEY, currentToken);
      }
    }
  }, [status, session]);

  // Refresh / cookie gone: NextAuth reports unauthenticated — run backend logout directly.
  useEffect(() => {
    if (status !== "unauthenticated") return;
    if (localStorage.getItem(GUEST_LOGIN_TRANSITION_KEY) === "1") {
      // Explicit guest -> login transition should not be treated as session expiry.
      localStorage.removeItem(GUEST_LOGIN_TRANSITION_KEY);
      localStorage.removeItem(SESSION_FLAG_KEY);
      localStorage.removeItem(USER_TOKEN_KEY);
      wasAuthenticatedRef.current = false;
      return;
    }
    if (!hadPriorSession(wasAuthenticatedRef.current)) return;
    void performSessionExpiredLogout();
  }, [status, performSessionExpiredLogout]);

  useEffect(() => {
    void reconcileStaleSession();
  }, [pathname, reconcileStaleSession]);

  // Stale "authenticated" in React while session-token cookie is already gone.
  useEffect(() => {
    if (status !== "authenticated") return;
    void reconcileStaleSession();
  }, [status, session, reconcileStaleSession]);

  // Natural JWT expiry while SessionProvider still shows authenticated.
  // Schedule a precise timer for the exact moment the session expires, so logout
  // fires immediately at expiry instead of waiting for the next 60s poll cycle.
  // The timer is rescheduled whenever `session.expires` changes (refresh, re-login).
  useEffect(() => {
    if (!session?.expires) return;
    const expiresAt = new Date(session.expires).getTime();
    const msUntilExpiry = expiresAt - Date.now();

    const runExpiredCheck = () => {
      if (logoutInProgressRef.current) return;
      if (!hadPriorSession(wasAuthenticatedRef.current)) return;
      void performSessionExpiredLogout();
    };

    if (msUntilExpiry <= 0) {
      runExpiredCheck();
      return;
    }

    const timer = setTimeout(runExpiredCheck, msUntilExpiry + EXPIRY_TIMER_BUFFER_MS);
    return () => clearTimeout(timer);
  }, [session?.expires, performSessionExpiredLogout]);

  // Short-interval safety net for cases where the precise setTimeout above
  // could be delayed (background-tab throttling, laptop sleep, clock drift).
  useEffect(() => {
    const checkExpiry = setInterval(() => {
      if (logoutInProgressRef.current) return;
      if (!hadPriorSession(wasAuthenticatedRef.current)) return;
      const s = sessionRef.current;
      if (!s?.expires) return;
      if (new Date(s.expires).getTime() < Date.now()) {
        void performSessionExpiredLogout();
      }
    }, EXPIRY_SAFETY_INTERVAL_MS);
    return () => clearInterval(checkExpiry);
  }, [performSessionExpiredLogout]);

  // Immediate checks without a short poll: navigation, tab focus, window focus
  useEffect(() => {
    const runCheck = () => {
      if (hadPriorSession(wasAuthenticatedRef.current)) {
        void reconcileStaleSession();
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") runCheck();
    };

    window.addEventListener("focus", runCheck);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", runCheck);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [reconcileStaleSession]);

  return null;
}
